/**
 * Dots & Boxes Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * and multiplayer stats.
 */

import {
  DotsBoxesStatistics,
  DotsBoxesGameResult,
  DotsBoxesGameStats,
  DotsBoxesDifficulty,
  DotsBoxesGameSettings,
  PlayerColor,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class DotsBoxesStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'dots_boxes_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): DotsBoxesStatistics {
    return {
      singlePlayer: {
        easy: this.createEmptyGameStats(),
        medium: this.createEmptyGameStats(),
        hard: this.createEmptyGameStats(),
      },
      localMultiplayer: this.createEmptyGameStats(),
      onlineMultiplayer: this.createEmptyGameStats(),
      totalGames: 0,
      totalPlayTime: 0,
    };
  }

  private createEmptyGameStats(): DotsBoxesGameStats {
    return {
      wins: 0,
      losses: 0,
      draws: 0,
      winPercentage: 0,
      averageScore: 0,
      highestScore: 0,
      totalBoxesClaimed: 0,
    };
  }

  getStatistics(userId?: string): DotsBoxesStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as DotsBoxesStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get dots-and-boxes statistics:', error);
      return this.createEmptyStats();
    }
  }

  recordGameResult(
    result: DotsBoxesGameResult,
    playerColor: PlayerColor,
    userId?: string
  ): boolean {
    try {
      const stats = this.getStatistics(userId);

      if (result.mode === 'singleplayer' && result.difficulty) {
        this.updateGameStats(stats.singlePlayer[result.difficulty], result, playerColor);
      } else if (result.mode === 'local-multiplayer') {
        this.updateGameStats(stats.localMultiplayer, result, playerColor);
      } else if (result.mode === 'multiplayer-realtime') {
        this.updateGameStats(stats.onlineMultiplayer, result, playerColor);
      }

      stats.totalGames++;
      stats.totalPlayTime += result.duration;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record dots-and-boxes game result:', error);
      return false;
    }
  }

  private updateGameStats(
    stats: DotsBoxesGameStats,
    result: DotsBoxesGameResult,
    playerColor: PlayerColor
  ): void {
    const totalGames = stats.wins + stats.losses + stats.draws;

    if (result.winner === 'draw') {
      stats.draws++;
    } else if (result.winner === playerColor) {
      stats.wins++;
    } else {
      stats.losses++;
    }

    const newTotalGames = stats.wins + stats.losses + stats.draws;
    stats.winPercentage = newTotalGames > 0 ? (stats.wins / newTotalGames) * 100 : 0;

    const playerScore = result.scores[playerColor] || 0;
    stats.averageScore =
      newTotalGames > 0
        ? (stats.averageScore * totalGames + playerScore) / newTotalGames
        : playerScore;

    if (playerScore > stats.highestScore) {
      stats.highestScore = playerScore;
    }

    stats.totalBoxesClaimed += playerScore;
  }

  getModeStatistics(
    mode: DotsBoxesGameSettings['mode'],
    difficulty?: DotsBoxesDifficulty,
    userId?: string
  ): DotsBoxesGameStats {
    const stats = this.getStatistics(userId);

    if (mode === 'singleplayer' && difficulty) {
      return stats.singlePlayer[difficulty];
    } else if (mode === 'local-multiplayer') {
      return stats.localMultiplayer;
    } else if (mode === 'multiplayer-realtime') {
      return stats.onlineMultiplayer;
    }

    return this.createEmptyGameStats();
  }

  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset dots-and-boxes statistics:', error);
      return false;
    }
  }
}
